import {
  type CampaignAnalyticsGranularity,
  type DashboardRange,
  type PortfolioAnalyticsResponse,
  type PortfolioAttachCampaignsResponseData,
  type PortfolioCreateInput,
  type PortfolioCreateResponseData,
  type PortfolioDetailResponse,
  type PortfolioEligibleCampaign,
  type PortfolioSummary,
  portfolioAnalyticsResponseSchema,
  portfolioAttachCampaignsResponseDataSchema,
  portfolioCreateResponseDataSchema,
  portfolioDetailResponseSchema,
  portfolioEligibleCampaignsResponseSchema,
  portfolioListResponseSchema,
} from "@adpilot/shared";
import { type ReportingPeriod, reportingPeriodSearchParams } from "@/stores/reporting-period";
import { api } from "./api";
import type { ReportingTimeZone } from "./reporting-time-zone";

/** Create a portfolio on Amazon for an explicitly selected shop (dialog flow). */
export async function createPortfolio(
  shopId: string,
  input: PortfolioCreateInput,
): Promise<PortfolioCreateResponseData> {
  const res = await api
    .post("portfolios", { json: input, headers: { "X-Shop-Id": shopId } })
    .json<{ success: true; data: unknown }>();
  return portfolioCreateResponseDataSchema.parse(res.data);
}

/** ADP-89: shop-scoped portfolio list (shop context is injected by the api client). */
/** `shopId` overrides the current shop, e.g. for a cross-shop campaign drill-down. */
export async function fetchPortfolios(shopId?: string | null): Promise<PortfolioSummary[]> {
  const res = await api
    .get("portfolios", shopId ? { headers: { "X-Shop-Id": shopId } } : undefined)
    .json<unknown>();
  return portfolioListResponseSchema.parse(res).data;
}

/** ADP-121: portfolio analytics (KPI + trend + per-portfolio rows). */
export async function fetchPortfolioAnalytics(
  range: ReportingPeriod,
  granularity: CampaignAnalyticsGranularity,
  timeZone: ReportingTimeZone,
  // ADP-110: aggregate scope; empty/omitted keeps the single-shop path.
  shopIds?: string[],
): Promise<PortfolioAnalyticsResponse> {
  const res = await api
    .get("portfolios/analytics", {
      searchParams: {
        ...reportingPeriodSearchParams(range, timeZone),
        granularity,
        ...(shopIds?.length ? { shopIds: shopIds.join(",") } : {}),
      },
    })
    .json<{ success: true; data: unknown }>();
  return portfolioAnalyticsResponseSchema.parse(res.data);
}

/** ADP-137:「添加广告活动」source list — the portfolio-shop's unattributed campaigns. */
export async function fetchEligibleCampaigns(
  portfolioId: string,
  range: DashboardRange,
): Promise<PortfolioEligibleCampaign[]> {
  const res = await api
    .get(`portfolios/${portfolioId}/eligible-campaigns`, { searchParams: { range } })
    .json<unknown>();
  return portfolioEligibleCampaignsResponseSchema.parse(res).data;
}

/** ADP-137: bulk-attach existing campaigns to the portfolio (per-campaign outcomes). */
export async function attachPortfolioCampaigns(
  portfolioId: string,
  campaignIds: string[],
): Promise<PortfolioAttachCampaignsResponseData> {
  const res = await api
    .post(`portfolios/${portfolioId}/campaigns`, { json: { campaignIds } })
    .json<{ success: true; data: unknown }>();
  return portfolioAttachCampaignsResponseDataSchema.parse(res.data);
}

/** ADP-121: single-portfolio detail (summary + KPI + trend + member campaigns). */
export async function fetchPortfolioDetail(
  id: string,
  range: ReportingPeriod,
  granularity: CampaignAnalyticsGranularity,
  timeZone: ReportingTimeZone,
): Promise<PortfolioDetailResponse> {
  const res = await api
    .get(`portfolios/${id}`, {
      searchParams: { ...reportingPeriodSearchParams(range, timeZone), granularity },
    })
    .json<{ success: true; data: unknown }>();
  return portfolioDetailResponseSchema.parse(res.data);
}
